import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Services", path: "/services" },
    { name: "Gallery", path: "/gallery" },
    { name: "Contact", path: "/contact" },
    { name: "Career", path: "https://pcworldraipur.in/newjoin/", isExternal: true }
  ];

  return (
    <>
      {/* 1. TOP BAR */}
      <div className={`fixed top-0 left-0 w-full z-[120] bg-slate-950 text-white h-10 hidden md:flex items-center border-b border-white/10 transition-transform duration-300 ${
        isScrolled ? "-translate-y-full" : "translate-y-0"
      }`}>
        <div className="max-w-7xl mx-auto px-6 w-full flex justify-between items-center text-[10px] font-black uppercase tracking-[0.2em]">
          <div className="flex gap-6 items-center">
            <div>📞 +91 93027 07699</div>
            <div className="text-slate-400">✉️ salesraipur@pcworldraipur.in</div>
          </div>
          <div className="flex-1 max-w-xs overflow-hidden relative mx-10">
            <motion.div 
              animate={{ x: ["100%", "-100%"] }} 
              transition={{ repeat: Infinity, duration: 15, ease: "linear" }}
              className="whitespace-nowrap text-blue-400 font-bold"
            >
              🚀 AUTHORIZED ENTERPRISE PARTNER — PREMIUM B2B SOLUTIONS
            </motion.div>
          </div>
        </div>
      </div>

      {/* 2. MAIN NAVBAR */}
      <nav className={`fixed left-0 w-full z-[110] transition-all duration-300 ${
        isScrolled ? "top-0 bg-white/95 backdrop-blur-md shadow-md py-3" : "top-0 md:top-10 bg-white py-4" 
      }`}>
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <Link to="/">
            <img 
              src="https://ik.imagekit.io/igxrggga8/pcworldraipur.in/logo/PC%20WORLD%20Horizontal%20Logo-Photoroom.png?updatedAt=1762158478332" 
              alt="PCWorld" 
              className="h-10 md:h-12 object-contain " 
            />
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((route) => (
              route.isExternal ? (
                <a key={route.path} href={route.path} target="_blank" rel="noopener noreferrer" className="text-[11px] font-black text-slate-700 hover:text-blue-600 uppercase tracking-widest">
                  {route.name}
                </a>
              ) : (
                <Link key={route.path} to={route.path} className={`text-[11px] font-black uppercase tracking-widest relative transition-colors ${location.pathname === route.path ? "text-blue-600" : "text-slate-700 hover:text-blue-600"}`}>
                  {route.name}
                  {location.pathname === route.path && (
                    <motion.div layoutId="active-pill" className="absolute -bottom-1 left-0 w-full h-0.5 bg-blue-600" />
                  )}
                </Link>
              )
            ))}
          </div>

          <button className="md:hidden text-slate-900" onClick={() => setIsOpen(true)}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-8 h-8"><path d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" /></svg>
          </button>
        </div>
      </nav>

      {/* 3. MOBILE MENU - Updated to support New Window for Career */}
      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} className="fixed inset-0 bg-white z-[200] flex flex-col p-6">
            <div className="flex justify-between items-center mb-10">
              <img src="https://ik.imagekit.io/igxrggga8/pcworldraipur.in/logo/PC%20WORLD%20Horizontal%20Logo-Photoroom.png" alt="PCWorld" className="h-10 " />
              <button onClick={() => setIsOpen(false)} className="p-2 text-slate-900 bg-slate-100 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className="w-6 h-6"><path d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="flex flex-col space-y-6">
              {navItems.map((route) => (
                route.isExternal ? (
                  /* Updated: Open External Link in New Tab on Mobile */
                  <a 
                    key={route.path} 
                    href={route.path} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={() => setIsOpen(false)}
                    className="text-4xl font-black uppercase tracking-tighter text-slate-900 flex items-center gap-3"
                  >
                    {route.name} <span className="text-xl text-blue-600">↗</span>
                  </a>
                ) : (
                  <Link 
                    key={route.path} 
                    to={route.path} 
                    onClick={() => setIsOpen(false)} 
                    className={`text-4xl font-black uppercase tracking-tighter ${location.pathname === route.path ? "text-blue-600" : "text-slate-900"}`}
                  >
                    {route.name}
                  </Link>
                )
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}